import i18n from './../../shared/i18n';
import BaseModel, {defineTranslatableProperties} from "../../core/components/input-widget/BaseModel";
import _ from 'lodash';
import moment from "moment";
import {SYSTEM_DATE_FORMAT} from "@/core/services/formatter.service";
import {store} from "@/plugins/vuex";

export default class CustomerSearch extends BaseModel {
  customer_no = null;
  first_name = null;
  last_name = null;
  company_name = null;
  personal_id = null;
  address_info = null;
  email = null;
  phone = null;
  tags = null;
  category_uuids = [];
  interest_category_uuids = [];
  activityDateRange = {startDate: null, endDate: null};
  optin = false;
  is_vendor = false;
  personal_identification_number = null;
  document_type = null;
  document_number = null;
  document_issue_date = {startDate: null, endDate: null};
  document_expiry_date = {startDate: null, endDate: null};
  date_of_birth = {startDate: null, endDate: null};
  citizenship_code = null;
  residency_code = null;
  country_of_birth_code = null;
  place_of_issue = null;
  company_reg_number = null;
  account_manager_uuid = null;
  post_code = null;
  account_manager = null
  gender = null
  accounting_code = null
  identity_verification = null
  kyc_status_verification = null
  created_at = null;
  created_at_date_range = {};
  updated_at = null;
  updated_at_date_range = {};

  created_by_uuid = null;
  updated_by_uuid = null;
  created_by = null;
  updated_by = null;
  client_tier = null;
  live_id = null;
  exclude_from_auto_payments = null;

  rules = {};

  attributeLabels = {
    customer_no: i18n.t('Customer'),
    first_name: i18n.t('First Name'),
    last_name: i18n.t('Last Name'),
    company_name: i18n.t('Company Name'),
    personal_id: i18n.t('Personal ID'),
    phone: i18n.t('Phone'),
    email: i18n.t('Email'),
    address_info: i18n.t('Address'),
    tags: i18n.t('Tags'),
    optin: i18n.t('Optin'),
    is_vendor: i18n.t('Vendor'),
    post_code: i18n.t('Post Code'),
    gender: i18n.t('Gender'),
    accounting_code: i18n.t('Accounting Code'),
    identity_verification: i18n.t('Identity Verification'),
    kyc_status_verification: i18n.t('Account/KYC Verification'),
    client_tier: i18n.t('Client Tier'),
    live_id: i18n.t('Live ID'),
    exclude_from_auto_payments: i18n.t('Exclude from Auto-Payments'),
  };


  attributePlaceholders = this.attributeLabels;

  constructor(data = {}) {
    super();
    Object.assign(this, {...data});
  }

  toJSON() {
    let data = super.toJSON();

    if (data.category_uuids) {
      data.category_uuids = data.category_uuids.join(',');
    }

    if (data.interest_category_uuids) {
      data.interest_category_uuids = data.interest_category_uuids.join(',');
    }

    if (this.activityDateRange.startDate) {
      data.activity_date_after = moment(this.activityDateRange.startDate).format(SYSTEM_DATE_FORMAT);
      data.activity_date_before = moment(this.activityDateRange.endDate).format(SYSTEM_DATE_FORMAT);
    }

    data.optin ? data.optin = 'true' : delete data.optin
    data.is_vendor ? data.is_vendor = 'true' : delete data.is_vendor

    delete data.activityDateRange;
    delete data.account_manager;

    if (store.state && store.state.global && store.state.global.identificationFieldsFilters) {
      let identificationFieldsFilters = store.state.global.identificationFieldsFilters.customers;
      if (identificationFieldsFilters && identificationFieldsFilters.length > 0) {
        data = this.removeIdentificationFields(data, identificationFieldsFilters)
        for (let field of identificationFieldsFilters) {
          if (field.type === 'checkbox') {
            data[field.key] ? data[field.key] = 'true' : delete data[field.key]
          } else if (field.type === "daterange") {
            if (data[field.key].startDate) {
              data[`${field.key}_after`] = moment(data[field.key].startDate).format(SYSTEM_DATE_FORMAT);
              data[`${field.key}_before`] = moment(data[field.key].endDate).format(SYSTEM_DATE_FORMAT);
            }
            delete data[field.key]
          }
        }
      } else {
        data = this.removeIdentificationFields(data, [])
      }
    } else {
      data = this.removeIdentificationFields(data, [])
    }

    if (!this.identity_verification) {
      delete data.identity_verification;
    }

    if (!this.kyc_status_verification) {
      delete data.kyc_status_verification;
    }

    if (data.created_at_date_range && data.created_at_date_range.startDate && data.created_at_date_range.endDate) {
      data.created_at_after = moment(data.created_at_date_range.startDate).format(SYSTEM_DATE_FORMAT);
      data.created_at_before = moment(data.created_at_date_range.endDate).format(SYSTEM_DATE_FORMAT);
    }

    if (!this.exclude_from_auto_payments) {
      delete data.exclude_from_auto_payments;
    }

    delete data.created_at_date_range
    delete data.created_at

    if (data.updated_at_date_range && data.updated_at_date_range.startDate && data.updated_at_date_range.endDate) {
      data.updated_at_after = moment(data.updated_at_date_range.startDate).format(SYSTEM_DATE_FORMAT);
      data.updated_at_before = moment(data.updated_at_date_range.endDate).format(SYSTEM_DATE_FORMAT);
    }

    delete data.updated_at_date_range
    delete data.updated_at

    delete data.created_by
    delete data.updated_by

    return data;
  }

  removeIdentificationFields(data, excludeInRemove = []) {
    const removeFields = [
      'personal_identification_number',
      'document_type',
      'document_number',
      'document_issue_date',
      'document_expiry_date',
      'date_of_birth',
      'citizenship_code',
      'residency_code',
      'country_of_birth_code',
      'place_of_issue',
      'company_reg_number',
    ]
    const fieldKeys = excludeInRemove.map(field => field.key)
    for (const field of removeFields) {
      if (!fieldKeys.includes(field)) {
        delete data[field]
      }
    }

    return data;
  }

  getFilteredData() {
    let returnData = [];

    const data = this.toJSON();

    for (let attr in data) {
      if (data[attr]) {
        let label = this.attributeLabels[attr];
        let value = _.capitalize(data[attr]);

        let text = `${label}: ${value}`;
        returnData.push({text, attr});
      }
    }

    return returnData;
  }
}

defineTranslatableProperties(CustomerSearch);
